import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBusinessConfig } from "@/lib/use-business-config";

type CheckStatus = "ok" | "degraded" | "down";

interface HealthCheck {
  name: string;
  status: CheckStatus;
  latency_ms: number;
  detail?: string;
}

interface HealthPayload {
  status: CheckStatus;
  timestamp: string;
  checks: HealthCheck[];
}

interface Props {
  /** Poll interval in ms. Defaults to 60s. Set to 0 to disable polling. */
  intervalMs?: number;
  /** When true, also shows a subtle "all systems normal" pill. Default false. */
  showWhenHealthy?: boolean;
  /**
   * Client-side latency threshold (ms) above which an otherwise "ok" check
   * is reclassified as "degraded". Lets ops tune sensitivity without a
   * server redeploy. Defaults to 1500ms (matches server default).
   */
  degradedLatencyMs?: number;
  /**
   * Client-side latency threshold (ms) above which an otherwise "ok" or
   * "degraded" check is reclassified as "down". Defaults to 5000ms.
   */
  downLatencyMs?: number;
}

/**
 * On-page status banner. Calls /api/public/health and — when a critical
 * resource (database, CMS config) fails to load — shows a visible,
 * dismissible banner listing exactly which checks are failing, so
 * visitors know something's up and can still reach the workshop by phone.
 * Silent when everything is green.
 */
export function StatusBanner({
  intervalMs = 60_000,
  showWhenHealthy = false,
  degradedLatencyMs = 1500,
  downLatencyMs = 5000,
}: Props) {
  const [state, setState] = useState<HealthPayload | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const cancelledRef = useRef(false);
  const business = useBusinessConfig();
  const phoneHref = business.phoneHref;
  const phoneDisplay = business.phone;

  const poll = useCallback(async () => {
    try {
      const res = await fetch("/api/public/health", {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      // 503 still returns JSON — parse regardless.
      const body = (await res.json()) as HealthPayload;
      if (!cancelledRef.current) {
        setState(body);
        setFetchFailed(false);
      }
    } catch {
      if (!cancelledRef.current) setFetchFailed(true);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    void poll();
    if (intervalMs > 0) {
      timer = setInterval(() => void poll(), intervalMs);
    }
    return () => {
      cancelledRef.current = true;
      if (timer) clearInterval(timer);
    };
  }, [intervalMs, poll]);

  const handleRetry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await poll();
    } finally {
      setRetrying(false);
    }
  }, [poll, retrying]);

  /**
   * Re-evaluate each check against the configured latency thresholds.
   * The server may have marked a check "ok" using its own defaults; we
   * escalate to "degraded" / "down" if it exceeds the client budget.
   * A check reported as "down" by the server always stays "down".
   */
  const evaluated = useMemo(() => {
    if (!state) return null;
    const checks: HealthCheck[] = state.checks.map((c) => {
      let status: CheckStatus = c.status;
      if (status !== "down" && c.latency_ms >= downLatencyMs) status = "down";
      else if (status === "ok" && c.latency_ms >= degradedLatencyMs) status = "degraded";
      return { ...c, status };
    });
    const overall: CheckStatus = checks.some((c) => c.status === "down")
      ? "down"
      : checks.some((c) => c.status === "degraded")
        ? "degraded"
        : "ok";
    return { ...state, status: overall, checks };
  }, [state, degradedLatencyMs, downLatencyMs]);

  if (dismissed) return null;

  // Network-level failure to reach /api/public/health itself.
  if (fetchFailed) {
    return (
      <Banner
        tone="warning"
        onDismiss={() => setDismissed(true)}
        onRetry={handleRetry}
        retrying={retrying}
      >
        <strong>Connection issue.</strong> We can't reach our systems right now. If a page looks
        wrong, please call{" "}
        <a href={phoneHref} className="underline font-semibold">
          the workshop
        </a>{" "}
        and we'll help you directly.
      </Banner>
    );
  }

  if (!evaluated) return null;

  if (evaluated.status === "ok") {
    if (!showWhenHealthy) return null;
    return (
      <Banner tone="ok" onDismiss={() => setDismissed(true)}>
        All systems normal.
      </Banner>
    );
  }

  const failing = evaluated.checks.filter((c) => c.status !== "ok");

  if (evaluated.status === "down") {
    return (
      <Banner
        tone="error"
        onDismiss={() => setDismissed(true)}
        onRetry={handleRetry}
        retrying={retrying}
      >
        <strong>Service disruption.</strong> Some parts of the site aren't loading properly. The
        workshop is still open — please call us on{" "}
        <a href={phoneHref} className="underline font-semibold">
          {phoneDisplay}
        </a>
        .
        <FailingList checks={failing} />
      </Banner>
    );
  }

  return (
    <Banner
      tone="warning"
      onDismiss={() => setDismissed(true)}
      onRetry={handleRetry}
      retrying={retrying}
    >
      <strong>Running slow.</strong> The site is responding slower than usual. If anything
      doesn't load, please refresh or call the workshop directly.
      <FailingList checks={failing} />
    </Banner>
  );
}

function FailingList({ checks }: { checks: HealthCheck[] }) {
  if (!checks.length) return null;
  return (
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-90">
      {checks.map((c) => (
        <li key={c.name} className="inline-flex items-center gap-1">
          <span
            aria-hidden="true"
            className={`inline-block h-1.5 w-1.5 rounded-full ${
              c.status === "down" ? "bg-destructive" : "bg-amber-500"
            }`}
          />
          <span className="font-semibold uppercase tracking-wider">{c.name}</span>
          <span className="opacity-75">
            {c.status} · {Math.round(c.latency_ms)}ms
            {c.detail ? ` · ${c.detail}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Banner({
  tone,
  onDismiss,
  onRetry,
  retrying,
  children,
}: {
  tone: "ok" | "warning" | "error";
  onDismiss: () => void;
  onRetry?: () => void;
  retrying?: boolean;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === "error"
      ? "bg-destructive/10 text-destructive border-destructive/30"
      : tone === "warning"
        ? "bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/30"
        : "bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 border-emerald-500/30";

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full border-b px-4 py-2 text-sm ${toneClass}`}
    >
      <div className="mx-auto flex max-w-6xl items-start justify-between gap-4">
        <div className="flex-1 leading-snug">{children}</div>
        <div className="flex items-center gap-2 shrink-0">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              aria-label="Retry health check now"
              aria-busy={retrying || undefined}
              className="rounded px-2 py-1 text-xs font-semibold uppercase tracking-wider opacity-80 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current disabled:cursor-not-allowed disabled:opacity-50"
            >
              {retrying ? "Retrying…" : "Retry now"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss status message"
            className="rounded px-2 py-1 text-xs font-semibold uppercase tracking-wider opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
